import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { STORAGE_KEYS } from "@constants";
import NavPermissionLink from "@/components/common/NavPermissionLink/NavPermissionLink";
import tallyReportsIcon from "@/assets/navBottomTally/tallyReports.svg";
import skuMappingIcon from "@/assets/navBottomTally/skuMapping.svg";
import "./NavBottomTally.css";

const NavBottomTally = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [logoError, setLogoError] = useState(false);

  // Get company data from localStorage
  const getCompanyFromStorage = () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.COMPANY_DATA);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  };

  // Get user data from localStorage
  const getUserFromStorage = () => {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  };

  const companyData = getCompanyFromStorage();
  const user = getUserFromStorage();

  // Get company logo URL
  const getCompanyLogoUrl = () => {
    const logoPath = companyData?.company_logo;
    if (!logoPath) return null;

    if (logoPath.startsWith('http')) {
      return logoPath;
    }

    const baseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace('/api', '');
    return `${baseUrl}${logoPath.startsWith('/') ? logoPath : `/${logoPath}`}`;
  };

  const companyLogoUrl = getCompanyLogoUrl();
  const companyName = companyData?.company_name;

  const visibleRoutes = [
    "/financial-dashboard",
    "/tally-dashboard",
    "/transactions",
    "/sales/distributors",
    "/tally/master/skus",
    "/tally-sync",
    "/secondary-sales",
    "/receivable",
    "/dashboard/receivables-v2",
    "/payable",
    "/sales-mapping",
    "/industry-categories",
    "/my-profile",
    "/saved-profiles",
    "/user-role-management",
    "/roles-and-permissions",
    "/labels",
    "/team-hierarchy"
  ];

  const shouldShow = visibleRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(route + "/")
  );

  useEffect(() => {
    if (shouldShow) {
      document.body.classList.add("show-nav-bottom-tally");
    } else {
      document.body.classList.remove("show-nav-bottom-tally");
    }
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  const navItems = [
    {
      id: "tally-reports",
      label: t("navBottomTally.tallyReports", "Tally Reports"),
      icon: tallyReportsIcon,
      path: "/tally-dashboard",
      isImage: true,
    },
    {
      id: "sku-mapping",
      label: t("navBottomTally.skuMapping", "SKU Mapping"),
      icon: skuMappingIcon,
      path: "/secondary-sales/send-request",
      isImage: true,
    },
    {
      id: "profile",
      label: t("navBottomTally.profile", "Profile"),
      icon: null, // Will be rendered dynamically
      path: "/my-profile",
      isImage: false,
      unrestricted: true,
    },
  ];

  const isActive = (path) => {
    // Special handling for Tally Reports - active for all tally-related routes
    if (path === "/tally-dashboard") {
      return location.pathname === "/tally-dashboard" || 
             location.pathname.startsWith("/transactions") ||
             location.pathname.startsWith("/sales/distributors") ||
             location.pathname.startsWith("/tally/master/skus") ||
             location.pathname.startsWith("/tally-sync") ||
             location.pathname.startsWith("/receivable") ||
             location.pathname.startsWith("/payable");
    }
    
    // Special handling for SKU Mapping
    if (path === "/secondary-sales/send-request") {
      return location.pathname.startsWith("/secondary-sales");
    }
    
    // Special handling for Profile
    if (path === "/my-profile") {
      return location.pathname === "/my-profile" || 
             location.pathname === "/saved-profiles";
    }
    
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleNavClick = (e, path) => {
    e.preventDefault();
    
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
    
    navigate(path);
  };

  return (
    <nav className="nav-bottom-tally">
      <div className="nav-bottom-tally__container">
        {navItems.map((item) => (
          <NavPermissionLink
            key={item.id}
            permissionCode={item.permissionCode}
            unrestricted={item.unrestricted}
            to={item.path}
            onClick={(e) => handleNavClick(e, item.path)}
            className={`nav-bottom-tally__item ${isActive(item.path) ? "nav-bottom-tally__item--active" : ""}`}
          >
            <div className="nav-bottom-tally__icon">
              {item.id === "profile" ? (
                // Profile icon: Always show company logo or initials (never static image)
                companyLogoUrl && !logoError ? (
                  <img 
                    src={companyLogoUrl} 
                    alt={companyName || 'Company Logo'}
                    className="nav-bottom-tally__profile-logo"
                    onError={() => setLogoError(true)}
                    onLoad={() => setLogoError(false)}
                  />
                ) : (
                  <div className="nav-bottom-tally__profile-initials">
                    {companyName?.[0]?.toUpperCase() ||
                      user?.name?.[0]?.toUpperCase() ||
                      user?.user_name?.[0]?.toUpperCase() ||
                      'C'}
                  </div>
                )
              ) : (
                // Other icons: Show as normal
                item.icon && <img src={item.icon} alt={item.label} />
              )}
            </div>
            <span className="nav-bottom-tally__label">{item.label}</span>
          </NavPermissionLink>
        ))}
      </div>
    </nav>
  );
};

export default NavBottomTally;
